import { oidcConfig } from 'Auth/shared/constants/auth'
import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import type { User } from 'oidc-client-ts'
import { LOCALSTORAGE_IDENTITY_PREFIX } from 'shared/constants/localStorageVariables'

let inMemoryAccessToken: string | undefined

const getOidcStorageToken = () => {
  const fromStorage = localStorage.getItem(
    `${LOCALSTORAGE_IDENTITY_PREFIX}user:${oidcConfig.authority}:${oidcConfig.client_id}`
  )
  try {
    const oidcStorage = JSON.parse(fromStorage ?? '{}')
    return typeof oidcStorage === 'object' && oidcStorage
      ? oidcStorage.access_token
      : undefined
  } catch {
    return undefined
  }
}

export const getAccessToken = () =>
  inMemoryAccessToken || window.app_token || getOidcStorageToken()

export const setAxiosDefaults = () => {
  const accessToken = getAccessToken()
  axios.defaults.baseURL = window.config.api.url
  axios.defaults.headers.common['x-api-version'] = '1.0'
  axios.defaults.headers.common['Ocp-Apim-Subscription-Key'] =
    window.config.api.subscription
  axios.defaults.headers.common['APIM-BBLCode'] = window.config.bblCode

  setBearerToken(accessToken)
}

export const setBearerToken = (token: string) => {
  if (token) {
    inMemoryAccessToken = token
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
  }
}

let interceptorSetup = false

export const setup401Interceptor = (
  signinSilent: () => Promise<User | null>
) => {
  if (interceptorSetup) return
  interceptorSetup = true

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & {
        _retry?: boolean
      }

      if (
        error.response?.status !== 401 ||
        !originalRequest ||
        originalRequest._retry
      ) {
        return Promise.reject(error)
      }

      originalRequest._retry = true

      const failedToken = originalRequest.headers?.['Authorization']

      const currentToken = axios.defaults.headers.common['Authorization']
      if (currentToken && currentToken !== failedToken) {
        originalRequest.headers = originalRequest.headers ?? {}
        originalRequest.headers['Authorization'] = currentToken as string
        return axios(originalRequest)
      }

      const user = await signinSilent()
      if (user?.access_token) {
        originalRequest.headers = originalRequest.headers ?? {}
        originalRequest.headers['Authorization'] = 'Bearer ' + user.access_token
        return axios(originalRequest)
      }

      return Promise.reject(error)
    }
  )
}
